import React from 'react';
import { GradientBorder } from 'components/common/GradientBorder';
import type { TFCardData } from 'data/analysisDisplay';

interface Props {
  data: TFCardData;
  isLast?: boolean;
}

export function TimeframeCard({ data, isLast }: Props) {
  const { role, name, isBullish, metrics, signals, gradientColors } = data;
  const hasSignals = signals.bullish.length + signals.bearish.length > 0;

  return (
    <GradientBorder
      colors={gradientColors}
      borderRadius={10}
      borderWidth={2}
      style={{ marginBottom: isLast ? 0 : 7 }}
    >
      {/* Header */}
      <div className="tf-head">
        <div>
          <div className="tf-role">{role}</div>
          <div className="tf-name">{name}</div>
        </div>
        <div className={`tf-badge ${isBullish ? 'badge-bull' : 'badge-bear'}`}>
          {isBullish ? '↑ Bull' : '↓ Bear'}
        </div>
      </div>

      {/* Metrics grid */}
      <div className="tf-metrics">
        {metrics.map((m, i) => (
          <div key={i} className="tf-metric">
            <div className="tf-metric-label">{m.label}</div>
            <div
              className="tf-metric-value"
              style={{
                ...(m.color ? { color: m.color } : null),
                ...(m.valueFontSize ? { fontSize: m.valueFontSize } : null),
              }}
            >
              {m.value}
            </div>
            {m.sub ? <div className="tf-metric-sub">{m.sub}</div> : null}
            {m.barPercent !== undefined && (
              <div className="bar-track">
                <div
                  className="bar-fill"
                  style={{
                    width: `${m.barPercent}%`,
                    backgroundColor: m.barColor ?? 'var(--cyan)',
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Signals */}
      {hasSignals && (
        <>
          <div className="tf-divider" />
          <div className="tf-signals">
            <div className="tf-signals-label">Active signals</div>

            {signals.bullish.length > 0 && (
              <>
                <div className="sig-group-bull">↑ Bullish</div>
                {signals.bullish.map((s, i) => (
                  <div key={`bull-${i}`} className="sig-row">
                    <span className="sig-dot" style={{ backgroundColor: s.color }} />
                    <span className="sig-text">{s.label}</span>
                    <span className={`sig-age${s.isNow ? ' sig-age-now' : ''}`}>{s.age}</span>
                  </div>
                ))}
              </>
            )}

            {signals.bearish.length > 0 && (
              <>
                <div
                  className="sig-group-bear"
                  style={signals.bullish.length > 0 ? { marginTop: 4 } : undefined}
                >
                  ↓ Bearish
                </div>
                {signals.bearish.map((s, i) => (
                  <div key={`bear-${i}`} className="sig-row">
                    <span className="sig-dot" style={{ backgroundColor: s.color }} />
                    <span className="sig-text">{s.label}</span>
                    <span className={`sig-age${s.isNow ? ' sig-age-now' : ''}`}>{s.age}</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </GradientBorder>
  );
}
